import React, { useEffect, useMemo } from 'react';
import { IDisburseAllDisbursementData } from 'types/order/escrow/IDisburseAllDisbursementData';
import { IDisburseAllResult } from 'types/order/escrow/IDisburseAllResult';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';
import { createDisbursementColumns } from './createDisbursementColumns';
import { HeaderText2TW } from 'tailwindComponents/HeaderTextTW/HeaderTextTW';
import { PreviewContainer } from '../printDocuments/PreviewContainer';
import { clientUrls } from 'ui-toolkit/rest/ClientUrlFactory';
import { LinkTW } from 'tailwindComponents/link/LinkTW';
import { AllDisbursementTables } from './AllDisbursmentTables';
import { disbursementByType } from '../edit/disbursements/disbursementsByType';
import { useGetTotals } from './useGetTotals';
import { Currency } from 'ui-toolkit';

export interface IDisbursementResultsProps {
  orderId: string;
  disbursements: IDisburseAllDisbursementData[];
  results: IDisburseAllResult;
}

export const DisbursementResults: React.FC<IDisbursementResultsProps> = ({ orderId, results, disbursements }) => {
  const getColumns = useMemo(() => createDisbursementColumns(false, undefined, results), [results]);

  const filteredDisbursements = useMemo(
    () => filterDisbursementsByType(disbursements, results),
    [disbursements, results]
  );

  const documentIdListFromInitiatedChecks = useMemo(() => {
    const initiatedChecks = results.manualChecks.filter((t) => t.status === 'initiated').map((t) => t.disbursementId);
    const documentIdList = filteredDisbursements.Check.filter((t) =>
      initiatedChecks.includes(t.disbursement.id)
    ).flatMap((t) => t.lineItem.statementFees.flatMap((t) => t.linkedDocumentIds));

    return documentIdList;
  }, [results, filteredDisbursements]);

  const { total } = useGetTotals(filteredDisbursements);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className={'p-2'}>
      <section className="flex flex-col gap-2">
        <div className="flex justify-between">
          <HeaderText2TW>
            Results of Disburse All: <Currency value={total} valueIsCents />
          </HeaderText2TW>
        </div>
      </section>
      <section>
        <PreviewContainer orderId={orderId} documentIds={documentIdListFromInitiatedChecks} />
        <div className="flex flex-col gap-4">
          <div>
            <AllDisbursementTables columns={getColumns} data={filteredDisbursements} />
          </div>
        </div>
      </section>
      <section className="h-18 fixed bottom-0 right-0 p-4 backdrop-blur">
        <div className="flex flex-col items-center justify-end gap-2">
          <div className={' grid grid-cols-2 text-right font-semibold'}>
            <div>Total Disbursements:</div>
            <div>
              <Currency value={total} valueIsCents />
            </div>
          </div>
          <div className={'flex justify-end gap-2 self-end'}>
            <LinkTW data-test-id="close-button" type="button" isButton to={clientUrls.urlFor.feesTab(orderId, 'a-j')}>
              Close
            </LinkTW>
          </div>
        </div>
      </section>
    </div>
  );
};

export const filterDisbursementsByType = (
  disbursements: IDisburseAllDisbursementData[],
  results: IDisburseAllResult
): {
  [key in Exclude<eDisbursementType, eDisbursementType.Unknown>]: IDisburseAllDisbursementData[];
} =>
  disbursements
    .filter(
      (item) =>
        isInResults(item.disbursement.id, results) ||
        (!disbursementByType[item.disbursement.type].isReadOnly(item.disbursement) && !item.disbursement.holdback)
    )
    .reduce(
      (filteredDisbursements, disbursement) => {
        const type = disbursement.disbursement.type;
        filteredDisbursements[type].push({
          ...disbursement,
          enabled: isInResults(disbursement.disbursement.id, results),
        });
        return filteredDisbursements;
      },
      {
        [eDisbursementType.ACH]: [],
        [eDisbursementType.Check]: [],
        [eDisbursementType.Wire]: [],
        [eDisbursementType.InternalTransfer]: [],
      } as { [key in Exclude<eDisbursementType, eDisbursementType.Unknown>]: IDisburseAllDisbursementData[] }
    );

const isInResults = (id: string, results: IDisburseAllResult) => {
  return !!(
    results.achs.find((t) => t.disbursementId === id) ??
    results.internalTransfers.find((t) => t.disbursementId === id) ??
    results.manualChecks.find((t) => t.disbursementId === id) ??
    results.wires.find((t) => t.disbursementId === id)
  );
};
